import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    AppBar,
    Box,
    Toolbar,
    Button,
    Menu,
    MenuItem,
    List, ListItem, ListItemButton, ListItemIcon,ListItemText, 
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import OrdersIcon from "@mui/icons-material/ShoppingCart";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LogoutIcon from "@mui/icons-material/Logout";
import InvoiceIcon from "@mui/icons-material/Receipt";
import EmailIcon from "@mui/icons-material/Email";
import TranslateIcon from "@mui/icons-material/Translate";
import ReportsIcon from "@mui/icons-material/BarChart";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";
import axiosExt from '../axios';
import i18n from "../i18n";
import logo from '../assets/DACustomsBlueGreenSmall.png';

interface LayoutProps {
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const roles: string[] = JSON.parse(localStorage.getItem("roles") || "[]");
    const isAdmin = roles.includes("admin");

    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(localStorage.getItem("language") || "en");

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("roles");
        navigate("/");
    };

    const [menuAnchor, setMenuAnchor] = useState<null | { anchor: HTMLElement, menu: string }>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, menuName: string) => {
        setMenuAnchor({ anchor: event.currentTarget, menu: menuName });
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    useEffect(() => {
        const userLang = localStorage.getItem("language") || "en";
        if (localStorage.getItem("token")) {
                setSelectedLanguage(userLang);
                i18n.changeLanguage(userLang);
        };

    }, []);

    const handleLanguageChange = async (lang: string) => {
        setSelectedLanguage(lang);
        i18n.changeLanguage(lang);
        localStorage.setItem("language", lang);
        
        if (localStorage.getItem("token")) {
            try {
                await axiosExt.post(
                    `${API_BASE_URL}/users/set-language/${localStorage.getItem("userId")}`,
                    { language: lang },
                    {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    }
                );
            } catch (error) {
                console.error("Error updating language in API", error);
            }
        }

        setLanguageMenuAnchor(null);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", width:"100vw" }}>

            {/* Top Navigation Bar */}
            <AppBar position="fixed">
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: "#0F0F10" }}>
                    {/* App Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img
                            src={logo}
                            alt="DA Customs2"
                            style={{ height: 40, marginRight: 16, flexGrow: 1, objectFit: "contain" }}
                        />
                    </Box>

                    {/* Main Menu Items */}
                    {/*<Button color="inherit" startIcon={<OrdersIcon />} onClick={(e) => handleMenuOpen(e, "orders")}>*/}
                    {/*    {t("Orders")}*/}
                    {/*</Button>*/}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button color="inherit" onClick={() => navigate("/orderdetailspage?view=grid")} startIcon={<OrdersIcon />}>
                            {t("Orders")}
                        </Button>

                        <Button color="inherit" onClick={() => navigate("/orderdetailspage?view=list")} startIcon={<VisibilityIcon />}>
                            {t("Details")}
                        </Button>

                        <Button color="inherit" onClick={() => navigate("/customers")} startIcon={<PeopleIcon />}>
                            {t("Customers")}
                        </Button>
                        <Button color="inherit" onClick={() => navigate("/contacts")} startIcon={<ContactPhoneIcon />}>
                            {t("Contacts")}
                        </Button>
                        <Button color="inherit" onClick={() => navigate("/emails")} startIcon={<EmailIcon />}>
                            {t("Emails")}
                        </Button>
                        <Button color="inherit" onClick={() => navigate("/invoices")} startIcon={<InvoiceIcon />}>
                            {t("Invoices")}
                        </Button>

                        {isAdmin && (
                            <>
                                <Button color="inherit" startIcon={<ReportsIcon />} onClick={(e) => handleMenuOpen(e, "reports")}>
                                    {t("Reports")}
                                </Button>

                          
                            </>
                        )}

                        {/* Admin Dropdown Menu */}
                        {isAdmin && (
                            <>
                                <Button color="inherit" startIcon={<AdminPanelSettingsIcon />} onClick={(e) => handleMenuOpen(e, "admin")}>
                                    {t("Admin")}
                                </Button>
                            
                            </>
                        )}

                        <List>
                            <ListItem disablePadding>
                                <ListItemButton onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}>
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 'unset', mr: 1 }}><TranslateIcon sx={{ color: 'inherit' }} /></ListItemIcon>
                                    <ListItemText primary={`${selectedLanguage.toUpperCase()}`} />
                                </ListItemButton>
                            </ListItem>
                        </List>

                        <Menu
                            anchorEl={menuAnchor?.anchor || null}
                            open={Boolean(menuAnchor)}
                            onClose={handleMenuClose}
                        >
                            {menuAnchor?.menu === "orders" && (
                                <>
                                    <MenuItem onClick={() => { navigate("/orderdetailspage?view=list"); handleMenuClose(); }}>{t("Orders Details")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/orderdetailspage?view=grid"); handleMenuClose(); }}>{t("Orders Table")}</MenuItem>
                                </>
                            )}
                            {menuAnchor?.menu === "reports" && (
                                <>
                                    <MenuItem onClick={() => { navigate("/accountingtranslations"); handleMenuClose(); }}>{t("Accounting Translations")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/reportordersstatus"); handleMenuClose(); }}>{t("Orders Status")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/reportorderstatusbyassigned"); handleMenuClose(); }}>{t("Status By Person")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/surveys"); handleMenuClose(); }}>{t("Survey Results")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/reportcustomerordersno"); handleMenuClose(); }}>{t("Orders Per Customer")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/reportnps"); handleMenuClose(); }}>{t("Customer NPS")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/reportreminders"); handleMenuClose(); }}>{t("Reminders")}</MenuItem>
                                </>
                            )}
                            {menuAnchor?.menu === "admin" && (
                                <>
                                    <MenuItem onClick={() => { navigate("/accountingtranslations"); handleMenuClose(); }}>{t("Accounting Translations")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/services"); handleMenuClose(); }}>{t("Services")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/ordertypes"); handleMenuClose(); }}>{t("Types")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/orderstates"); handleMenuClose(); }}>{t("Order States")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/detailstates"); handleMenuClose(); }}>{t("Detail States")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/taxtypemappings"); handleMenuClose(); }}>{t("Tax Type Mappings")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/ignorelist"); handleMenuClose(); }}>{t("Ignore List")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/users"); handleMenuClose(); }}>{t("Users")}</MenuItem>
                                    <MenuItem onClick={() => { navigate("/messageTemplates"); handleMenuClose(); }}>{t("Message Templates")}</MenuItem>
                                </>
                            )}
                        </Menu>
                        {/* Language Menu */}
                        <Menu
                            anchorEl={languageMenuAnchor}
                            open={Boolean(languageMenuAnchor)}
                            onClose={() => setLanguageMenuAnchor(null)}
                        >
                            <MenuItem onClick={() => handleLanguageChange("en")}>English (EN)</MenuItem>
                            <MenuItem onClick={() => handleLanguageChange("de")}>Deutsch (DE)</MenuItem>
                            <MenuItem onClick={() => handleLanguageChange("pl")}>Polski (PL)</MenuItem>
                            <MenuItem onClick={() => handleLanguageChange("ro")}>Romana (RO)</MenuItem>
                        </Menu>

                        {/* Logout Button */}
                        <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
                            Logout
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Box component="main" sx={{ mt: 8, p: 1 }}>
                {children}
            </Box>
        </Box>
    );
};

export default Layout;
